import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Mail, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "sonner";

const formatErr = (d) => {
  if (!d) return "Erreur";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || "").filter(Boolean).join(" ") || "Erreur";
  return d?.msg || "Erreur";
};

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="forgot-password-page"
      className="min-h-screen flex items-center justify-center px-4"
      style={{ backgroundColor: "#F9F8F6" }}
    >
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <img src="/logo-yacoclean.jpg" alt="YACOCLEAN" data-testid="forgot-logo" className="w-24 h-24 rounded-2xl object-cover mb-4 shadow-sm" />
          <h1 className="font-heading text-3xl font-bold tracking-tight">YACOCLEAN</h1>
          <p className="text-sm text-[#5C665E] mt-1">Suivi de factures</p>
        </div>

        <div className="rounded-2xl border bg-white p-8" style={{ borderColor: "#E5E2DC" }}>
          {sent ? (
            <div className="text-center" data-testid="forgot-password-sent">
              <div
                className="w-14 h-14 rounded-full mx-auto flex items-center justify-center mb-4"
                style={{ backgroundColor: "#DFF2E4", color: "#1E6534" }}
              >
                <CheckCircle2 size={24} />
              </div>
              <h2 className="font-heading text-xl font-semibold mb-2">Email envoyé</h2>
              <p className="text-sm text-[#5C665E] mb-6">
                Si cet email est enregistré, un lien de réinitialisation vient d&apos;être envoyé.
                <br />Vérifiez votre boîte de réception (et le dossier <b>spam</b>).
                <br /><br />
                Le lien est valable <b>1 heure</b>.
              </p>
              <Link
                to="/login"
                data-testid="back-to-login-link"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#2C5545] hover:underline"
              >
                <ArrowLeft size={14} /> Retour à la connexion
              </Link>
            </div>
          ) : (
            <>
              <h2 className="font-heading text-xl font-semibold mb-1">Mot de passe oublié ?</h2>
              <p className="text-sm text-[#5C665E] mb-6">
                Saisissez votre email de connexion. Vous recevrez un lien pour définir un nouveau mot de passe.
              </p>

              <form onSubmit={onSubmit} className="space-y-4">
                <div>
                  <Label
                    htmlFor="email"
                    className="text-xs uppercase tracking-wider text-[#5C665E] mb-1.5 block"
                  >
                    Email de connexion
                  </Label>
                  <Input
                    id="email"
                    data-testid="forgot-password-email-input"
                    type="email"
                    required
                    autoFocus
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="vous@entreprise.fr"
                    className="bg-white border-[#E5E2DC] h-11"
                  />
                </div>

                <button
                  type="submit"
                  data-testid="forgot-password-submit"
                  disabled={loading}
                  className="w-full rounded-full py-3 text-sm font-semibold text-white btn-lift focus-brand flex items-center justify-center gap-2 disabled:opacity-60"
                  style={{ backgroundColor: "#2C5545" }}
                >
                  <Mail size={16} />
                  {loading ? "Envoi..." : "Envoyer le lien"}
                </button>
              </form>

              <div className="mt-6 text-center">
                <Link
                  to="/login"
                  data-testid="back-to-login-link"
                  className="inline-flex items-center gap-1.5 text-sm text-[#5C665E] hover:text-[#1A1D1A]"
                >
                  <ArrowLeft size={14} /> Retour à la connexion
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
      <Toaster position="top-right" richColors closeButton />
    </div>
  );
}
